import { useCallback, useEffect, useMemo, useRef, useState } from "react";import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useWebSocket } from "@/hooks/useWebSocket";
import { useMessageNotifications } from "@/hooks/useMessageNotifications";
import { useNotificationSoundPref } from "@/hooks/useNotificationSoundPref";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useAppBadge } from "@/hooks/useAppBadge";
import TopBar from "@/components/TopBar";
import ConversationItem from "@/components/ConversationItem";
import Thread from "@/components/Thread";
import Composer from "@/components/Composer";
import ConversationDetails from "@/components/ConversationDetails";
import SofiPanel from "@/components/SofiPanel";
import KimKimTripLink from "@/components/KimKimTripLink";
import ArchiveConversationDialog from "@/components/ArchiveConversationDialog";
import FirstTouchConfirmDialog from "@/components/FirstTouchConfirmDialog";
import DeleteMessageDialog from "@/components/DeleteMessageDialog";
import AudioReadinessBanner from "@/components/AudioReadinessBanner";
import PushOnboardingBanner from "@/components/PushOnboardingBanner";
import InboxMobile from "@/components/mobile/InboxMobile";
import { Avatar } from "@/components/Avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Fire, Spinner, Tray, MagnifyingGlass, X, Archive, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { debugLog } from "@/lib/debugFlags";

export default function InboxPage() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const [conversations, setConversations] = useState([]);
    const [users, setUsers] = useState([]);
    const [searchQ, setSearchQ] = useState("");
    const [loadingList, setLoadingList] = useState(true);

    const [selectedId, setSelectedId] = useState(searchParams.get("conversation_id") || null);
    const [selectedDetail, setSelectedDetail] = useState(null); // { conversation, messages, notes, customer, trip }
    const [loadingDetail, setLoadingDetail] = useState(false);

    // Phase 2.4: poll the global outbound kill-switch state so the composer can
    // surface a banner when admin paused outbound. Cheap GET, every 8s.
    const [outboundState, setOutboundState] = useState({ paused: false, smtp_enabled: true });
    useEffect(() => {
        if (user?.role !== "admin") return;  // only admins need the badge; others see outbound_allowed gating only
        const load = () => api.get("/admin/outbound/status").then(({ data }) => setOutboundState(data)).catch(() => {});
        load();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
    }, [user?.role]);

    const usersById = useMemo(() => {
        const m = {};
        users.forEach(u => { m[u.id] = u; });
        return m;
    }, [users]);

    // Phase 3.6 — 3-state archived filter, forwarded to /conversations
    // as a server-side query param. Persisted in localStorage. Declared
    // BEFORE reloadList since reloadList's useCallback closes over it.
    const [archivedFilter, setArchivedFilter] = useState(() => {
        try {
            const v = localStorage.getItem("hub.inbox.archived_filter");
            return v === "true" || v === "all" ? v : "false";
        } catch { return "false"; }
    });
    useEffect(() => {
        try { localStorage.setItem("hub.inbox.archived_filter", archivedFilter); }
        catch { /* ignore */ }
    }, [archivedFilter]);

    const reloadList = useCallback(async () => {
        try {
            // Phase 3.3: search is client-side (by customer name only), so
            // we no longer forward `q` / `filter` params to the backend.
            // Phase 3.6: `archived` IS forwarded (server-side filter).
            const { data } = await api.get("/conversations", {
                params: { archived: archivedFilter },
            });
            setConversations(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cargar el inbox");
        } finally {
            setLoadingList(false);
        }
    }, [archivedFilter]);

    useEffect(() => { reloadList(); }, [reloadList]);

    // Client-side filter by customer name only. Case-insensitive substring
    // match. Empty query = full list.
    const filteredConversations = useMemo(() => {
        const q = searchQ.trim().toLowerCase();
        if (!q) return conversations;
        return conversations.filter(c => (c.customer_name || "").toLowerCase().includes(q));
    }, [conversations, searchQ]);

    useEffect(() => {
        api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    }, []);

    const loadDetail = useCallback(async (convId) => {
        if (!convId) { setSelectedDetail(null); return; }
        setLoadingDetail(true);
        try {
            const { data } = await api.get(`/conversations/${convId}`);
            setSelectedDetail(data);
            // mark as read locally and on server
            await api.post(`/conversations/${convId}/read`).catch(() => {});
            setConversations(prev =>
                prev.map(c => c.id === convId ? { ...c, unread_count: 0 } : c)
            );
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cargar la conversación");
            setSelectedDetail(null);
        } finally {
            setLoadingDetail(false);
        }
    }, []);

    useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

    // Keep URL in sync with selected conversation
    useEffect(() => {
        const current = searchParams.get("conversation_id");
        if (selectedId && current !== selectedId) {
            setSearchParams({ conversation_id: selectedId }, { replace: true });
        }
        if (!selectedId && current) {
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    // ==================== NOTIFICATIONS (declared before WS handler) ====================
    // Keep refs of conversations/selectedId so the hook callbacks stay stable
    // and don't remount the notifier on every list update.
    const convsRef = useRef(conversations);
    useEffect(() => { convsRef.current = conversations; }, [conversations]);
    const selectedIdRef = useRef(selectedId);
    useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

    const getConversationTitle = useCallback((evt) => {
        const c = convsRef.current.find(x => x.id === evt.conversation_id);
        const senderName = evt.message?.sender_name;
        if (c?.customer_name && senderName && senderName !== c.customer_name) {
            return `${senderName} · ${c.customer_name}`;
        }
        return c?.customer_name || senderName || "Nuevo mensaje";
    }, []);

    const onNotificationClick = useCallback((evt) => {
        setSelectedId(evt.conversation_id);
    }, []);

    const [soundEnabled] = useNotificationSoundPref();
    const [archiveOpen, setArchiveOpen] = useState(false);

    const { notify } = useMessageNotifications({
        getConversationTitle,
        onNotificationClick,
        soundEnabled,
    });

    // Phase 7.3 — iOS home-screen icon badge (Badging API). Sum of unread
    // counts across every visible conversation. When the number changes
    // (new inbound, mark-as-read, archive) we push it to the OS so the
    // app icon on the home screen mirrors it. Silent no-op on browsers
    // without the Badging API (i.e. everything that isn't Chrome 81+ /
    // iOS 16.4+ PWA-installed).
    const totalUnread = useMemo(
        () => conversations.reduce((s, c) => s + (c.unread_count || 0), 0),
        [conversations],
    );
    useAppBadge(totalUnread);

    // WebSocket: real-time updates
    const onWsEvent = useCallback((evt) => {
        if (evt.type === "message.new") {
            // Fire desktop / favicon / sound notifications for inbound
            // messages. The hook itself guards visibility, direction and
            // permission, and treats the currently-open conversation as
            // "already seen" via the shouldNotify predicate below.
            const shouldFire = evt.message?.direction === "in"
                && evt.conversation_id !== selectedIdRef.current;
            if (shouldFire) notify(evt);

            // Append to detail if it's the open one. DEDUP by _id to survive
            // multiple WS connections (multi-tab / hot-reload ghosts) that all
            // deliver the same broadcast.
            setSelectedDetail(prev => {
                if (!prev) return prev;
                const convDoc = prev.conversation || {};
                if ((convDoc._id || convDoc.id) !== evt.conversation_id) return prev;
                const incomingId = evt.message?._id;
                const already = (prev.messages || []).some(m => m._id === incomingId);
                if (already) return prev;
                return { ...prev, messages: [...(prev.messages || []), evt.message] };
            });
            // Update list row preview + unread
            setConversations(prev => {
                const idx = prev.findIndex(c => c.id === evt.conversation_id);
                const msg = evt.message;
                const preview = (msg?.body || "").slice(0, 140);
                if (idx < 0) {
                    // Maybe a new conversation we don't have in the list — reload
                    reloadList();
                    return prev;
                }
                const next = [...prev];
                const current = { ...next[idx] };
                current.last_message_preview = preview;
                current.last_message_at = msg?.created_at || new Date().toISOString();
                if (msg?.direction === "in" && evt.conversation_id !== selectedId) {
                    current.unread_count = (current.unread_count || 0) + 1;
                }
                next.splice(idx, 1);
                next.unshift(current);
                return next;
            });
            // Toast for inbound when not open. Uses `toast.info` (rich-coloured
            // brand-blue variant of sonner) instead of the neutral `toast()`
            // because Eduardo reported the neutral variant blends into the
            // white background and gets missed. Duration bumped from the 4 s
            // default → 6 s so a busy operator has time to notice + click.
            if (evt.message?.direction === "in" && evt.conversation_id !== selectedId) {
                const conv = conversations.find(c => c.id === evt.conversation_id);
                const senderName = evt.message?.sender_name;
                const title = conv?.customer_name
                    ? `Nuevo mensaje de ${conv.customer_name}`
                    : senderName
                        ? `Nuevo mensaje de ${senderName}`
                        : "Nuevo mensaje";
                debugLog("notif", "Toast firing:", title, "conv=", evt.conversation_id);
                toast.info(title, {
                    description: (evt.message?.body || "").slice(0, 100),
                    duration: 6000,
                    action: {
                        label: "Abrir",
                        onClick: () => setSelectedId(evt.conversation_id),
                    },
                });
            } else {
                debugLog("notif", "Toast SKIPPED —",
                    evt.message?.direction !== "in" ? "outbound message" :
                    "conversation is already open");
            }
        } else if (evt.type === "note.new") {
            setSelectedDetail(prev => {
                if (!prev) return prev;
                const convDoc = prev.conversation || {};
                if ((convDoc._id || convDoc.id) !== evt.conversation_id) return prev;
                const incomingId = evt.note?._id;
                const already = (prev.notes || []).some(n => n._id === incomingId);
                if (already) return prev;
                return { ...prev, notes: [...(prev.notes || []), evt.note] };
            });
        } else if (evt.type === "conversation.updated") {
            // Update list row + open detail
            setConversations(prev =>
                prev.map(c => c.id === evt.conversation_id ? { ...c, ...(evt.changes || {}) } : c)
            );
            setSelectedDetail(prev => {
                if (!prev) return prev;
                const convDoc = prev.conversation || {};
                if ((convDoc._id || convDoc.id) === evt.conversation_id) {
                    return { ...prev, conversation: { ...convDoc, ...(evt.changes || {}) } };
                }
                return prev;
            });
        } else if (evt.type === "message.deleted") {
            // Fase 11 — otro cliente (o este) borró un mensaje: quitarlo
            // del thread en vivo sin recargar la conversación.
            setSelectedDetail(prev => {
                if (!prev) return prev;
                const convDoc = prev.conversation || {};
                if ((convDoc._id || convDoc.id) !== evt.conversation_id) return prev;
                return {
                    ...prev,
                    messages: (prev.messages || []).filter(m => m._id !== evt.message_id),
                };
            });
        }
    }, [selectedId, conversations, reloadList]);

    const { status: wsStatus } = useWebSocket(onWsEvent, !!user);

    // -------- actions --------

    // Phase 5 — First-touch confirmation state. When the user tries to send
    // the FIRST message of their session to a real KimKim trip (not the
    // historical POC th1DXPSB), we intercept and show a modal so the agent
    // must positively confirm the recipient. Prevents the "typo, escribí al
    // cliente equivocado" class of accident.
    const [firstTouchState, setFirstTouchState] = useState({
        open: false,
        body: "",
        trip_title: "",
        customer_name: "",
        submitting: false,
    });

    // Fase 11 — estado del modal de borrado. `target` es el mensaje a borrar
    // (referencia directa, no id — usamos su body para el preview).
    const [deleteMessageTarget, setDeleteMessageTarget] = useState(null);

    const onDeleteMessage = useCallback(async () => {
        const msg = deleteMessageTarget;
        if (!msg || !selectedId) return;
        try {
            const { data } = await api.delete(
                `/conversations/${selectedId}/messages/${msg._id}`
            );
            // Optimistic local update — el WS broadcast también llegará
            // pero puede tardar 200-400ms, y queremos que el operador vea
            // el cambio inmediato.
            setSelectedDetail(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    messages: (prev.messages || []).filter(m => m._id !== msg._id),
                };
            });
            if (data.deleted_in_hub && data.deleted_in_kimkim) {
                toast.success("Mensaje borrado", {
                    description: "También borrado en KimKim.",
                });
            } else if (data.deleted_in_hub && !data.deleted_in_kimkim) {
                toast.warning("Mensaje borrado en el Hub", {
                    description: `No se pudo borrar en KimKim (${data.kimkim_error || "error desconocido"}).`,
                    duration: 8000,
                });
            } else {
                toast.error("Error al borrar el mensaje");
            }
        } catch (e) {
            toast.error("Error al borrar el mensaje", {
                description: formatApiErrorDetail(e) || e.message,
            });
            throw e;
        }
    }, [deleteMessageTarget, selectedId]);

    const _doActualSend = useCallback(async (body) => {
        // The pure "hit backend + toast" primitive shared by both the direct
        // send path and the "user confirmed the first-touch modal" path.
        const detail = selectedDetail;
        const isKimkim = detail?.conversation?.channel === "kimkim";
        const outboundOn = !!detail?.conversation?.outbound_allowed;
        const clientName = detail?.customer?.name;
        try {
            await api.post(`/conversations/${selectedId}/messages`, { body });
            if (isKimkim && outboundOn) {
                toast.success(
                    clientName
                        ? `Mensaje enviado a ${clientName}`
                        : "Mensaje enviado al chat de KimKim"
                );
            }
            return true;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo enviar el mensaje");
            return false;
        }
    }, [selectedId, selectedDetail]);

    const onSendMessage = useCallback(async (body) => {
        if (!selectedId) return;
        const detail = selectedDetail;
        const isKimkim = detail?.conversation?.channel === "kimkim";

        // Only KimKim outbound triggers the first-touch rail — manual channel
        // has no risk of hitting a real customer, and other channels are not
        // wired yet.
        if (isKimkim) {
            try {
                const { data } = await api.get(`/conversations/${selectedId}/first-touch-status`);
                if (data && data.is_first_touch) {
                    setFirstTouchState({
                        open: true,
                        body,
                        trip_title: data.trip_title || detail?.trip?.title || "",
                        customer_name: data.customer_name || detail?.customer?.name || "",
                        submitting: false,
                    });
                    return;   // wait for the modal confirm/cancel
                }
            } catch (e) {
                // Pre-check is best-effort; if it fails we still let the
                // backend gate the send. Silently fall through.
                console.warn("[first-touch-status] pre-check failed:", e?.message || e);
            }
        }
        await _doActualSend(body);
    }, [selectedId, selectedDetail, _doActualSend]);

    const onFirstTouchConfirm = useCallback(async () => {
        setFirstTouchState(s => ({ ...s, submitting: true }));
        const ok = await _doActualSend(firstTouchState.body);
        setFirstTouchState({
            open: false, body: "", trip_title: "", customer_name: "", submitting: false,
        });
        // If the backend rejected the send, the toast fired inside _doActualSend
        // already told the user. If it accepted, next composer render will see
        // is_first_touch=false and skip the modal.
        if (!ok) {
            // Preserve the composer text so the agent can retry after checking
            // credentials / network — nothing to do here, textarea state lives
            // in Composer and we didn't touch it.
        }
    }, [_doActualSend, firstTouchState.body]);

    const onFirstTouchCancel = useCallback(() => {
        setFirstTouchState({
            open: false, body: "", trip_title: "", customer_name: "", submitting: false,
        });
    }, []);

    const onAddNote = useCallback(async (body) => {
        if (!selectedId) return;
        try {
            await api.post(`/conversations/${selectedId}/notes`, { body, mentions: [] });
            toast.success("Nota interna guardada");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar la nota");
        }
    }, [selectedId]);

    const patchConv = useCallback(async (changes) => {
        if (!selectedId) return;
        try {
            const { data } = await api.patch(`/conversations/${selectedId}`, changes);
            setSelectedDetail(prev => prev ? { ...prev, conversation: data } : prev);
            setConversations(prev => prev.map(c => c.id === selectedId ? { ...c, ...changes } : c));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo actualizar");
        }
    }, [selectedId]);

    const conv = selectedDetail?.conversation;
    const headerCustomer = selectedDetail?.customer;
    const headerTrip = selectedDetail?.trip;
    const isViewer = user?.role === "viewer";

    const isMobile = useIsMobile();

    return (
        <div className="h-full flex flex-col bg-slate-50">
            {/* TopBar hides itself on mobile via `hidden md:flex` on the header
                (see TopBar.jsx). The mobile top strip is rendered inside
                InboxMobile with its own back / info / overflow controls. */}
            {!isMobile && <TopBar wsStatus={wsStatus} onSearch={setSearchQ} search={searchQ} />}
            <AudioReadinessBanner soundEnabled={soundEnabled} />
            <PushOnboardingBanner />

            {isMobile ? (
                <InboxMobile
                    user={user}
                    users={users}
                    usersById={usersById}
                    conversations={conversations}
                    filteredConversations={filteredConversations}
                    selectedId={selectedId}
                    setSelectedId={setSelectedId}
                    selectedDetail={selectedDetail}
                    loadingList={loadingList}
                    loadingDetail={loadingDetail}
                    searchQ={searchQ}
                    setSearchQ={setSearchQ}
                    archivedFilter={archivedFilter}
                    setArchivedFilter={setArchivedFilter}
                    outboundState={outboundState}
                    reloadList={reloadList}
                    onSendMessage={onSendMessage}
                    onAddNote={onAddNote}
                    patchConv={patchConv}
                    setArchiveOpen={setArchiveOpen}
                    wsStatus={wsStatus}
                    onOpenDeleteMessage={(msg) => setDeleteMessageTarget(msg)}
                />
            ) : (
            <div className="flex-1 grid min-h-0 overflow-hidden" style={{ gridTemplateColumns: "minmax(280px,360px) 1fr minmax(280px,340px)", gridTemplateRows: "minmax(0, 1fr)" }}>
                {/* ---------- LEFT: list ---------- */}
                <aside className="border-r border-slate-200 bg-white flex flex-col min-w-0" data-testid="inbox-list-panel">
                    <div className="p-3 border-b border-slate-200 space-y-2">
                        <div className="relative">
                            <MagnifyingGlass size={14} weight="bold"
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                            <input
                                type="search"
                                value={searchQ}
                                onChange={(e) => setSearchQ(e.target.value)}
                                placeholder="Buscar por nombre de cliente..."
                                className="w-full pl-8 pr-8 py-2 text-sm bg-slate-50 border border-slate-200 rounded-md
                                           focus:bg-white focus:border-brand-400 focus:ring-1 focus:ring-brand-200
                                           focus:outline-none transition-colors"
                                data-testid="conversation-search-input"
                            />
                            {searchQ && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQ("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
                                    aria-label="Limpiar búsqueda"
                                    data-testid="conversation-search-clear"
                                >
                                    <X size={12} weight="bold" />
                                </button>
                            )}
                        </div>

                        {/* Phase 3.6 — 3-state archived filter, persisted via localStorage
                            (hub.inbox.archived_filter). Segmented pills with the
                            currently-selected one in brand-blue. */}
                        <div className="flex items-center gap-1" data-testid="archived-filter-group">
                            {[
                                { id: "false", label: "Activas" },
                                { id: "true",  label: "Archivadas" },
                                { id: "all",   label: "Todas" },
                            ].map(opt => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => setArchivedFilter(opt.id)}
                                    className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                                        archivedFilter === opt.id
                                            ? "bg-brand-600 border-brand-600 text-white"
                                            : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                                    }`}
                                    data-testid={`archived-filter-${opt.id}`}
                                    aria-pressed={archivedFilter === opt.id}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto vh-scroll" data-testid="conversation-list">
                        {loadingList && (
                            <div className="p-6 flex items-center justify-center text-slate-400" data-testid="conversation-list-loading">
                                <Spinner className="animate-spin mr-2" /> Cargando…
                            </div>
                        )}
                        {!loadingList && filteredConversations.length === 0 && (
                            <div className="p-10 flex flex-col items-center text-slate-400 text-sm gap-2 text-center px-6" data-testid="conversation-list-empty">
                                {searchQ ? <MagnifyingGlass size={28}/> : <Tray size={28}/>}
                                {searchQ
                                    ? <span>{`Sin conversaciones que coincidan con «${searchQ}»`}</span>
                                    : <span>No hay conversaciones</span>}
                            </div>
                        )}
                        {filteredConversations.map(c => (
                            <ConversationItem
                                key={c.id}
                                conv={c}
                                active={c.id === selectedId}
                                onClick={() => setSelectedId(c.id)}
                                currentUserId={user?.id}
                                usersById={usersById}
                            />
                        ))}
                    </div>
                </aside>

                {/* ---------- MIDDLE: thread + composer ---------- */}
                <main className="flex flex-col min-w-0 min-h-0" data-testid="inbox-thread-panel">
                    {!conv && !loadingDetail && (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center" data-testid="thread-no-selection">
                            <Tray size={48} className="mb-3"/>
                            <div className="font-display font-semibold text-slate-600 text-lg">
                                Selecciona una conversación
                            </div>
                            <div className="text-sm mt-1 max-w-sm">
                                Elige una conversación del listado de la izquierda para ver el hilo, responder o añadir una nota interna para tu equipo.
                            </div>
                        </div>
                    )}
                    {loadingDetail && !conv && (
                        <div className="flex-1 flex items-center justify-center text-slate-400" data-testid="thread-loading">
                            <Spinner className="animate-spin mr-2" /> Cargando conversación…
                        </div>
                    )}
                    {conv && (
                        <>
                            {/* Sticky Trip header — Phase 3.3. Anchored to the top
                                of the thread scroll region so it stays visible as
                                the agent scrolls through long conversations. */}
                            <div className="sticky top-0 z-20 border-b border-slate-200 bg-white shrink-0"
                                 data-testid="thread-header">
                                <div className="px-4 py-3 flex items-center gap-3 min-w-0">
                                    <Avatar name={headerCustomer?.name} size={40}/>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-display font-bold text-lg text-slate-900 truncate flex items-center gap-2"
                                             data-testid="thread-header-title">
                                            <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px] shrink-0
                                                             bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                                                Trip
                                            </span>
                                            <span className="truncate"
                                                  title={(() => {
                                                      const t = (headerTrip?.title || headerTrip?.external_refs?.kimkim_trip_slug || "—").trim();
                                                      const n = (headerCustomer?.name || "").trim();
                                                      return t && n && t.toLowerCase() !== n.toLowerCase() ? `${t} · ${n}` : t;
                                                  })()}>
                                                {(() => {
                                                    const t = (headerTrip?.title || headerTrip?.external_refs?.kimkim_trip_slug || "—").trim();
                                                    const n = (headerCustomer?.name || "").trim();
                                                    // Phase 3.5 — collapse the "<trip> · <customer>" into a single
                                                    // token when both strings match (case-insensitive), e.g. the demo
                                                    // "Hub · Bienvenida" trip where title == customer.name.
                                                    const isSame = t && n && t.toLowerCase() === n.toLowerCase();
                                                    return isSame ? (
                                                        <>{t || "—"}</>
                                                    ) : (
                                                        <>
                                                            {t || "—"}
                                                            {n && (<><span className="text-slate-400 mx-1.5">·</span><span className="text-slate-700">{n}</span></>)}
                                                        </>
                                                    );
                                                })()}
                                            </span>
                                            {conv.urgency === "urgent" && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded shrink-0">
                                                    <Fire size={10} weight="fill"/> Urgente
                                                </span>
                                            )}
                                        </div>
                                        {(headerTrip?.destination || conv.channel) && (
                                            <div className="text-xs text-slate-500 truncate mt-0.5">
                                                {headerTrip?.destination}
                                                {headerTrip?.destination && conv.channel ? " · " : ""}
                                                <span className="uppercase tracking-wider font-semibold">
                                                    {conv.channel === "kimkim" ? "KimKim" : conv.channel === "manual" ? "Manual" : conv.channel}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Admin archive / unarchive button — visible for admins on
                                        every conversation regardless of channel. Archive is a soft
                                        delete (reversible) and it ONLY affects ViajaHub (never
                                        touches KimKim), so the tooltip below explicitly says so
                                        to eliminate any ambiguity for the operator. */}
                                    {user?.role === "admin" && (
                                        conv.archived ? (
                                            <TooltipProvider delayDuration={150}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                try {
                                                                    await api.post(`/conversations/${conv.id}/unarchive`);
                                                                    toast.success("Desarchivada, vuelve al inbox activo");
                                                                    reloadList();
                                                                } catch (e) {
                                                                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo desarchivar");
                                                                }
                                                            }}
                                                            className="shrink-0 h-9 px-3 rounded-md border border-emerald-200 text-emerald-700 bg-white hover:bg-emerald-50 hover:border-emerald-300 flex items-center gap-2 text-sm font-medium transition-colors"
                                                            aria-label="Desarchivar conversación"
                                                            data-testid="thread-header-unarchive-btn"
                                                        >
                                                            <ArrowClockwise size={20} weight="regular" />
                                                            <span>Desarchivar</span>
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="bottom" className="max-w-[220px] text-center">
                                                        Vuelve al inbox activo. Solo actúa en ViajaHub, no toca KimKim.
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        ) : (
                                            <TooltipProvider delayDuration={150}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => setArchiveOpen(true)}
                                                            className="shrink-0 h-9 px-3 rounded-md border border-slate-200 text-slate-700 bg-white hover:bg-slate-100 hover:border-slate-300 flex items-center gap-2 text-sm font-medium transition-colors"
                                                            aria-label="Archivar conversación"
                                                            data-testid="thread-header-archive-btn"
                                                        >
                                                            <Archive size={20} weight="regular" />
                                                            <span>Archivar</span>
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="bottom" className="max-w-[220px] text-center">
                                                        Solo archiva en ViajaHub, no toca KimKim.
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )
                                    )}
                                </div>
                            </div>

                            <Thread
                                messages={selectedDetail.messages}
                                notes={selectedDetail.notes}
                                customer={headerCustomer}
                                usersById={usersById}
                                currentUserId={user?.id}
                                onDeleteMessage={(msg) => setDeleteMessageTarget(msg)}
                            />

                            <Composer
                                onSendMessage={onSendMessage}
                                onAddNote={onAddNote}
                                disabled={isViewer || conv.status === "closed"}
                                sendDisabled={
                                    conv.channel === "kimkim" &&
                                    (!conv.outbound_allowed || outboundState.paused)
                                }
                                sendDisabledReason={
                                    conv.channel === "kimkim" && outboundState.paused
                                        ? "Envíos a clientes pausados por el admin (kill-switch). Reanúdalos en /admin/channels para volver a enviar."
                                        : (conv.channel === "kimkim" && !conv.outbound_allowed
                                            ? "⚠️ Solo lectura — este trip no está autorizado para envíos bajo el modo actual."
                                            : undefined)
                                }
                                sendDisabledSeverity={
                                    conv.channel === "kimkim" && !conv.outbound_allowed && !outboundState.paused
                                        ? "critical"
                                        : "warning"
                                }
                                // Phase 5 — El rail legacy "una vez por sesión" queda inhibido
                                // porque first-touch (per-user, per-trip) ya cubre el caso "primer
                                // envío" de forma más granular. Ver InboxPage.onSendMessage.
                                requireConfirm={false}
                            />

                            {/*
                             * Phase 5 — solo mostrar el banner POC cuando el
                             * modo outbound es whitelist_only. Cuando el admin
                             * activa mode=all_kimkim, el modo POC ya no aplica
                             * y este banner solo generaría confusión visual.
                             */}
                            {conv.is_poc_phase && outboundState.outbound_mode !== "all_kimkim" && (
                                <div className="text-center py-1.5 text-[11px] bg-amber-50 border-t border-amber-200 text-amber-800"
                                     data-testid="poc-phase-banner">
                                    Modo POC activo · whitelist de outbound = <code>th1DXPSB</code>
                                </div>
                            )}
                            {conv.status === "closed" && (
                                <div className="text-center py-1.5 text-[11px] text-slate-500 bg-slate-100 border-t border-slate-200" data-testid="thread-closed-banner">
                                    Conversación cerrada. Cambia el estado a «Abierta» para responder.
                                </div>
                            )}
                            {isViewer && (
                                <div className="text-center py-1.5 text-[11px] text-slate-500 bg-slate-100 border-t border-slate-200" data-testid="thread-viewer-banner">
                                    Modo solo lectura (rol viewer).
                                </div>
                            )}
                        </>
                    )}
                </main>

                {/* ---------- RIGHT: details ---------- */}
                <aside className="border-l border-slate-200 bg-white min-w-0" data-testid="inbox-details-panel">
                    {!conv && (
                        <div className="h-full p-6 text-slate-400 text-sm flex items-center justify-center text-center">
                            Selecciona una conversación para ver detalles del cliente y del viaje.
                        </div>
                    )}
                    {conv && (
                        <div className="h-full overflow-y-auto vh-scroll" data-testid="right-panel-scroll">
                            {/* Fase 9.3.1 — enlace directo al chat de KimKim.
                                Se muestra ANTES que SofiPanel para que el operador
                                lo tenga a mano incluso cuando SOFI no está matched. */}
                            {headerTrip && (headerTrip.external_refs?.kimkim_trip_slug
                                || headerTrip.external_refs?.kimkim_trip_slug_original_case) && (
                                <div className="px-4 pt-4 pb-2 border-b border-slate-100">
                                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-2">
                                        KimKim · Chat original
                                    </div>
                                    <KimKimTripLink trip={headerTrip} />
                                </div>
                            )}
                            {/* SOFI panel goes FIRST so reservations are always visible without scrolling. */}
                            <div className="px-4 pt-4 pb-2 border-b border-slate-100">
                                <SofiPanel conversation={conv} customer={headerCustomer} />
                            </div>
                            <ConversationDetails
                                conversation={conv}
                                trip={headerTrip}
                                customer={headerCustomer}
                                users={users}
                                currentUser={user}
                                onChangeStatus={(s) => patchConv({ status: s })}
                                onChangeAssignee={(uid) => patchConv({ assigned_user_id: uid })}
                                onToggleUrgent={() => patchConv({ urgency: conv.urgency === "urgent" ? "normal" : "urgent" })}
                            />
                        </div>
                    )}
                </aside>
            </div>
            )}

            <ArchiveConversationDialog
                open={archiveOpen}
                onOpenChange={setArchiveOpen}
                conversation={conv}
                trip={headerTrip}
                customer={headerCustomer}
                onArchived={(archivedId) => {
                    // Respect the current filter: if we're on "Activas" the row
                    // must vanish; on "Archivadas"/"Todas" it stays but with
                    // the archived badge applied. reloadList takes care of that.
                    setConversations(cs =>
                        archivedFilter === "false"
                            ? cs.filter(c => c.id !== archivedId)
                            : cs.map(c => c.id === archivedId ? { ...c, archived: true } : c)
                    );
                    setSelectedId(null);
                    reloadList();
                }}
            />

            <FirstTouchConfirmDialog
                open={firstTouchState.open}
                onOpenChange={(v) => {
                    if (!v) onFirstTouchCancel();
                }}
                trip_title={firstTouchState.trip_title}
                customer_name={firstTouchState.customer_name}
                preview={firstTouchState.body}
                onConfirm={onFirstTouchConfirm}
                onCancel={onFirstTouchCancel}
                submitting={firstTouchState.submitting}
            />

            <DeleteMessageDialog
                open={!!deleteMessageTarget}
                onOpenChange={(v) => { if (!v) setDeleteMessageTarget(null); }}
                message={deleteMessageTarget}
                onConfirm={onDeleteMessage}
            />
        </div>
    );
}
